import { useState } from "react";
import { supabase } from "../supabaseClient";
import { S, css } from "../theme";

function Pricing({ user }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // STRIPE PRICE IDS — LIVE MODE
  const PRICE_MONTHLY = "price_1TeOwKQn2EDKnJRr3bc0lHiB";
  const PRICE_ANNUAL  = "price_1TeOx0Qn2EDKnJRrHXGErerz";

  async function startCheckout(priceId) {
    setError("");
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        setError("Please log in again to subscribe.");
        setLoading(false);
        return;
      }
      const res = await fetch("/.netlify/functions/create-checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + token,
        },
        body: JSON.stringify({
          priceId,
          userId: user.id,
          userEmail: user.email,
          userTier: user.tier,
          origin: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Something went wrong. Please try again.");
        setLoading(false);
        return;
      }
      // Redirect to Stripe-hosted checkout
      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      setError("Could not start checkout. Please try again.");
      setLoading(false);
    }
  }

  async function openCustomerPortal() {
    setError("");
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const res = await fetch("/.netlify/functions/customer-portal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + token,
        },
        body: JSON.stringify({
          stripeCustomerId: user.stripe_customer_id,
          origin: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Could not open billing portal.");
        setLoading(false);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      setError("Could not open billing portal. Please try again.");
      setLoading(false);
    }
  }

  // ─── Founding Member view ─────────────────────────────────
  if (user?.tier === "founding_member") {
    return (
      <div style={{ padding: 40, maxWidth: 720, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ fontSize: "2.4rem", marginBottom: 12, opacity: 0.85 }}>🌿</div>
          <h2 style={{ ...css.fontDisplay, fontSize: "2.2rem", fontWeight: 400, fontStyle: "italic", color: S.ink, letterSpacing: "-0.02em", marginBottom: 14, lineHeight: 1.1 }}>
            You were here first.
          </h2>
          <h2 style={{ ...css.fontDisplay, fontSize: "2.2rem", fontWeight: 400, color: S.ink, letterSpacing: "-0.02em", marginBottom: 22, lineHeight: 1.1 }}>
            You're not paying.
          </h2>
          <p style={{ color: S.muted, fontSize: "0.98rem", lineHeight: 1.7, maxWidth: 480, margin: "0 auto", fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>
            You signed up before public launch — that makes you a Founding Member. Your account stays free, for as long as we exist.
          </p>
        </div>

        <div style={{ background: S.white, border: "1px solid " + S.border, borderRadius: 18, padding: 32, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ background: S.accent2, color: "#fff", fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.65rem", padding: "5px 12px", borderRadius: 100, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Founding Member
            </span>
          </div>
          <p style={{ color: S.ink2, fontSize: "0.92rem", lineHeight: 1.7, marginBottom: 0 }}>
            You have full access to every feature, every update, forever. Nothing here will ask you for a card.
          </p>
        </div>

        <p style={{ fontSize: "0.78rem", color: S.muted, textAlign: "center", lineHeight: 1.7, fontStyle: "italic" }}>
          Thank you for being early. It matters more than we can say.
        </p>
      </div>
    );
  }

  // ─── Existing Pro subscriber view ─────────────────────────
  if (user?.tier === "pro") {
    return (
      <div style={{ padding: 40, maxWidth: 720, margin: "0 auto" }}>
        <h2 style={{ ...css.fontDisplay, fontSize: "1.8rem", fontWeight: 500, color: S.ink, letterSpacing: "-0.02em", marginBottom: 8 }}>You're on Pro</h2>
        <p style={{ color: S.muted, fontSize: "0.9rem", marginBottom: 28, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>
          Full access. Thank you for being here.
        </p>

        <div style={{ background: S.white, border: "1px solid " + S.border, borderRadius: 18, padding: 28, marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: "1.1rem", color: S.ink }}>TrackMyPeptide Pro</div>
              <div style={{ fontSize: "0.82rem", color: S.muted, marginTop: 2 }}>
                Status: <span style={{ color: S.accent, fontWeight: 600 }}>{user.subscription_status || "active"}</span>
                {user.subscription_current_period_end && (
                  <> · renews {new Date(user.subscription_current_period_end).toLocaleDateString()}</>
                )}
              </div>
            </div>
          </div>
          <button onClick={openCustomerPortal} disabled={loading} style={{ padding: "12px 22px", borderRadius: 100, border: "1.5px solid " + S.border, background: "transparent", color: S.ink, fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "0.88rem", cursor: loading ? "wait" : "pointer" }}>
            {loading ? "Opening…" : "Manage subscription"}
          </button>
        </div>

        {error && <p style={{ color: S.accent2, fontSize: "0.85rem", marginTop: 12 }}>{error}</p>}
      </div>
    );
  }

  // ─── Default view: choose a plan ────────────────────────────
  const isTrial = user?.subscription_status === "trialing";
  const isExpired = !user?.tier || user?.tier === "free" || user?.subscription_status === "canceled" || user?.subscription_status === "past_due" || user?.subscription_status === "incomplete_expired";

  const featureList = [
    "Unlimited peptide tracking",
    "Full biomarker dashboard",
    "32+ peptide profiles",
    "Unlimited AI guidance",
    "Injection-site rotation map",
    "Community access",
    "Priority FDA alerts",
  ];

  return (
    <div style={{ padding: 40, maxWidth: 880, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 36 }}>
        <h2 style={{ ...css.fontDisplay, fontSize: "2rem", fontWeight: 500, color: S.ink, letterSpacing: "-0.02em", marginBottom: 10 }}>
          {isTrial ? "You're on your free trial" : "Choose your plan"}
        </h2>
        <p style={{ color: S.muted, fontSize: "0.95rem", maxWidth: 480, margin: "0 auto", fontFamily: "'Fraunces', serif", fontStyle: "italic", lineHeight: 1.6 }}>
          {isTrial
            ? "Full access for 7 days. Pick a plan anytime before your trial ends."
            : "Every plan includes a 7-day free trial. Cancel anytime."
          }
        </p>
      </div>

      {error && (
        <div style={{ background: S.accent2Light, border: "1px solid " + S.accent2 + "44", borderRadius: 12, padding: "12px 16px", marginBottom: 22, color: S.accent2, fontSize: "0.86rem" }}>
          {error}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 32 }}>
        {/* Monthly */}
        <div style={{ background: S.white, border: "1px solid " + S.border, borderRadius: 20, padding: 32, display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: "0.7rem", fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: S.muted, marginBottom: 14 }}>Monthly</div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontFamily: "'Fraunces', serif", fontSize: "2.8rem", fontWeight: 500, color: S.ink, letterSpacing: "-0.03em" }}>$29</span>
            <span style={{ fontSize: "0.92rem", color: S.muted }}> / month</span>
          </div>
          <p style={{ fontSize: "0.85rem", color: S.muted, marginBottom: 22, paddingBottom: 22, borderBottom: "1px solid " + S.border, lineHeight: 1.6, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>
            7-day free trial, then $29/mo. Cancel anytime.
          </p>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px 0", display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
            {featureList.map(f => (
              <li key={f} style={{ fontSize: "0.85rem", color: S.ink2, display: "flex", gap: 10, alignItems: "flex-start", lineHeight: 1.5 }}>
                <span style={{ color: S.accent, fontWeight: 600, flexShrink: 0 }}>✓</span>{f}
              </li>
            ))}
          </ul>
          <button onClick={() => startCheckout(PRICE_MONTHLY)} disabled={loading} style={{ width: "100%", padding: 15, borderRadius: 100, border: "none", background: S.accent, color: "#fff", fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.92rem", cursor: loading ? "wait" : "pointer", boxShadow: "0 6px 20px rgba(126,171,142,0.25)", letterSpacing: "0.01em" }}>
            {loading ? "Opening Stripe…" : "Start free trial — $29/mo"}
          </button>
        </div>

        {/* Annual */}
        <div style={{ background: S.white, border: "1.5px solid " + S.accent2 + "55", borderRadius: 20, padding: 32, position: "relative", display: "flex", flexDirection: "column", boxShadow: "0 0 0 1px " + S.accent2 + "22" }}>
          <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: S.accent2, color: "#fff", fontFamily: "'Fraunces', serif", fontStyle: "italic", fontWeight: 500, fontSize: "0.68rem", padding: "5px 14px", borderRadius: 100, letterSpacing: "0.06em" }}>
            best value — save $58/yr
          </div>
          <div style={{ fontSize: "0.7rem", fontWeight: 500, letterSpacing: "0.15em", textTransform: "uppercase", color: S.accent2, marginBottom: 14 }}>Annual</div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontFamily: "'Fraunces', serif", fontSize: "2.8rem", fontWeight: 500, color: S.ink, letterSpacing: "-0.03em" }}>$290</span>
            <span style={{ fontSize: "0.92rem", color: S.muted }}> / year</span>
          </div>
          <p style={{ fontSize: "0.85rem", color: S.muted, marginBottom: 22, paddingBottom: 22, borderBottom: "1px solid " + S.border, lineHeight: 1.6, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>
            7-day free trial, then $290/yr. Two months free vs. monthly.
          </p>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px 0", display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
            {[...featureList, "2 months free vs. monthly"].map(f => (
              <li key={f} style={{ fontSize: "0.85rem", color: S.ink2, display: "flex", gap: 10, alignItems: "flex-start", lineHeight: 1.5 }}>
                <span style={{ color: S.accent, fontWeight: 600, flexShrink: 0 }}>✓</span>{f}
              </li>
            ))}
          </ul>
          <button onClick={() => startCheckout(PRICE_ANNUAL)} disabled={loading} style={{ width: "100%", padding: 15, borderRadius: 100, border: "none", background: S.accent2, color: "#fff", fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.92rem", cursor: loading ? "wait" : "pointer", boxShadow: "0 6px 20px rgba(217,122,74,0.3)", letterSpacing: "0.01em" }}>
            {loading ? "Opening Stripe…" : "Start free trial — $290/yr"}
          </button>
        </div>
      </div>

      <p style={{ fontSize: "0.78rem", color: S.muted, lineHeight: 1.7, textAlign: "center", maxWidth: 520, margin: "0 auto", fontStyle: "italic", fontFamily: "'Fraunces', serif" }}>
        Your card is required to start the trial but won't be charged until day 8. Secure payment through Stripe. Cancel anytime from your account settings.
      </p>
    </div>
  );
}


// ─── LEGAL PAGES ─────────────────────────────────────────────

export { Pricing };
